import { API_BASE } from '../http';
import type { RequestFn } from '../http';
import type {
  TechnicianRequest,
  CreateTechnicianRequestData,
  ReviewTechnicianRequestData,
  SendOfferData,
  RespondToOfferData,
  MarkOrderedData,
  LinkWeclappData,
  MyTechnicianRequestsResponse,
  TechnicianRequestStatusCounts,
  AssignedTechnicianRequestView,
} from '../types';

export function createTechnicianRequestsApi(request: RequestFn) {
  return {
    async createTechnicianRequest(data: CreateTechnicianRequestData) {
      return request<{ technicianRequest: TechnicianRequest }>('/technician-requests', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getMyTechnicianRequests() {
      return request<MyTechnicianRequestsResponse>('/technician-requests/my');
    },

    /** @param signature PNG-Data-URL der digitalen Unterschrift (optional). */
    async confirmTechnicianRequestReceipt(id: string, signature?: string) {
      return request<{ technicianRequest: TechnicianRequest }>(
        `/technician-requests/${id}/confirm-receipt`,
        { method: 'PATCH', body: JSON.stringify(signature ? { signature } : {}) },
      );
    },

    /** Anfragender zieht die eigene Anfrage zurück. */
    async cancelMyTechnicianRequest(id: string, reason?: string) {
      return request<{ technicianRequest: TechnicianRequest; wasOrdered: boolean }>(
        `/technician-requests/${id}/cancel`,
        { method: 'PATCH', body: JSON.stringify({ reason }) },
      );
    },

    /** Admin storniert eine Anfrage — auch nach Freigabe oder Bestellung. */
    async cancelTechnicianRequestAsAdmin(id: string, reason?: string) {
      return request<{ technicianRequest: TechnicianRequest; wasOrdered: boolean }>(
        `/admin/technician-requests/${id}/cancel`,
        { method: 'PATCH', body: JSON.stringify({ reason }) },
      );
    },

    async getAdminTechnicianRequests(status?: string) {
      const query = status && status !== 'ALL' ? `?status=${encodeURIComponent(status)}` : '';
      return request<{
        requests: TechnicianRequest[];
        pendingCount: number;
        approvedCount: number;
        /** Zähler je Tab — unabhängig vom aktiven Filter */
        counts: TechnicianRequestStatusCounts;
      }>(`/admin/technician-requests${query}`);
    },

    async reviewTechnicianRequest(id: string, data: ReviewTechnicianRequestData) {
      return request<{ technicianRequest: TechnicianRequest }>(
        `/admin/technician-requests/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    async markTechnicianRequestOrdered(id: string, data: MarkOrderedData = {}) {
      return request<{ technicianRequest: TechnicianRequest; ticketCreated: boolean; ticketError: string | null }>(
        `/admin/technician-requests/${id}/mark-ordered`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    /** Bestellung einem Mitarbeiter zuweisen (nur Admin) — legt eine Aufgabe mit täglicher Erinnerung an */
    async assignTechnicianRequestOrder(
      id: string,
      data: { assigneeUserId: string; due: { date: string; time?: string | null }; note?: string | null },
    ) {
      return request<{ todoId: string; reassigned: boolean }>(`/admin/technician-requests/${id}/assign-order`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /** Anfrage lesend — für den, dem die Bestellung zugewiesen ist */
    async getAssignedTechnicianRequest(id: string) {
      return request<{ request: AssignedTechnicianRequestView }>(`/technician-requests/${id}/assigned-view`);
    },

    async sendTechnicianRequestOffer(id: string, data: SendOfferData) {
      return request<{ technicianRequest: TechnicianRequest }>(
        `/admin/technician-requests/${id}/send-offer`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    async respondToTechnicianRequestOffer(id: string, data: RespondToOfferData) {
      return request<{ technicianRequest: TechnicianRequest }>(
        `/technician-requests/${id}/respond-offer`,
        { method: 'PATCH', body: JSON.stringify(data) }
      );
    },

    async linkTechnicianRequestWeclapp(id: string, data: LinkWeclappData) {
      return request<{ technicianRequest: TechnicianRequest }>(
        `/admin/technician-requests/${id}/link-weclapp`,
        { method: 'PATCH', body: JSON.stringify(data) }
      );
    },

    // ── Verlauf: Kommentare + Anhänge (portal-intern, kein Weclapp-Ticket) ───────
    async getTechnicianRequestThread(id: string) {
      return request<TechnicianRequestThread>(`/technician-requests/${id}/thread`);
    },

    async addTechnicianRequestComment(
      id: string,
      data: { body: string; adminOnly?: boolean; attachmentIds?: string[] }
    ) {
      return request<{ comment: TechnicianRequestComment }>(
        `/technician-requests/${id}/comments`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    async deleteTechnicianRequestComment(id: string, commentId: string) {
      return request<{ success: boolean }>(
        `/technician-requests/${id}/comments/${commentId}`,
        { method: 'DELETE' }
      );
    },

    async uploadTechnicianRequestAttachment(
      id: string,
      data: { base64: string; filename?: string; mimeType?: string }
    ) {
      return request<{ attachment: TechnicianRequestAttachment }>(
        `/technician-requests/${id}/attachments`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    /** Download-/Anzeige-URL eines Anhangs (berechtigungsgeprüft serverseitig). */
    getTechnicianRequestAttachmentUrl(id: string, attachmentId: string) {
      return `${API_BASE}/technician-requests/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`;
    },
  };
}

export interface TechnicianRequestComment {
  id: string;
  body: string;
  adminOnly: boolean;
  createdAt: string;
  author: { id: string; displayName: string };
}

export interface TechnicianRequestAttachment {
  id: string;
  commentId?: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  uploadedBy?: { id: string; displayName: string };
}

export interface TechnicianRequestThread {
  comments: TechnicianRequestComment[];
  attachments: TechnicianRequestAttachment[];
}
